// client/src/components/ProductManagement/ProductCreate.jsx
import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import heic2any from 'heic2any';
import '../../css/ProductManagement/ProductCreate.css';

function computeRefundPolicyPreview(consumerPrice) {
  const c = Number(consumerPrice) || 0;
  if (c < 30000) return { label: '박스가의 60%', basis: 'box', rate: 60 };
  if (c < 50000) return { label: '소비자가의 60%', basis: 'consumer', rate: 60 };
  if (c < 200000) return { label: '소비자가의 70%', basis: 'consumer', rate: 70 };
  return { label: '소비자가의 80%', basis: 'consumer', rate: 80 };
}

/** ───────────────── 확률 자동 규칙 ─────────────────
 * 카테고리명(박스명)은 서버에서 넘어오는 box.name 과 정확히 일치해야 함
 * 예) '5,000원 박스', '10,000원 박스'
 */
const PROB_RULES = {
  '5,000원 박스': [
    { min: 0,       max: 10000,    p: 65 },
    { min: 10000,   max: 50000,    p: 21 },
    { min: 50000,   max: 100000,   p: 5 },
    { min: 100000,  max: 200000,   p: 0.29 },
    { min: 200000,  max: 300000,   p: 0.035 },
    { min: 300000,  max: 400000,   p: 0.01 },
    { min: 400000,  max: 500000,   p: 0.06 },
    { min: 500000,  max: 600000,   p: 0.02 },
    { min: 600000,  max: 700000,   p: 0.01 },
    { min: 700000,  max: 800000,   p: 0.005 },
    { min: 800000,  max: 900000,   p: 0.002 },
    { min: 900000,  max: Infinity, p: 0 },
  ],
  '10,000원 박스': [
    { min: 10000,   max: 20000,    p: 83.685 },
    { min: 20000,   max: 30000,    p: 11 },
    { min: 30000,   max: 40000,    p: 3.2 },
    { min: 40000,   max: 50000,    p: 0.5 },
    { min: 50000,   max: 60000,    p: 0.45 },
    { min: 60000,   max: 70000,    p: 0.14 },
    { min: 70000,   max: 80000,    p: 0.11 },
    { min: 80000,   max: 90000,    p: 0.1 },
    { min: 90000,   max: 100000,   p: 0.1 },
    { min: 100000,  max: 200000,   p: 0.45 },
    { min: 200000,  max: 300000,   p: 0.02 },
    { min: 300000,  max: 400000,   p: 0.015 },
    { min: 400000,  max: 500000,   p: 0.1 },
    { min: 500000,  max: 600000,   p: 0.04 },
    { min: 600000,  max: 700000,   p: 0.02 },
    { min: 700000,  max: 800000,   p: 0.015 },
    { min: 800000,  max: 900000,   p: 0.01 },
    { min: 900000,  max: 1000000,  p: 0.01 },
    { min: 1000000, max: 2000000,  p: 0.03 },
    { min: 2000000, max: 3000000,  p: 0.005 },
    { min: 3000000, max: Infinity, p: 0 },
  ],
};

const isAutoProbCategory = (category) => !!PROB_RULES[category];

function computeAutoProbability(category, consumerPrice) {
  const price = Number(consumerPrice) || 0;
  const rules = PROB_RULES[category];
  if (!rules) return null;
  const band = rules.find(r => price >= r.min && price < r.max);
  return typeof band?.p === 'number' ? band.p : null;
}

function findBandLabel(category, consumerPrice) {
  const price = Number(consumerPrice) || 0;
  const rules = PROB_RULES[category];
  if (!rules) return '-';
  const b = rules.find(r => price >= r.min && price < r.max);
  if (!b) return '-';
  const fmt = v => (v === Infinity ? '이상' : `${(v/10000).toFixed(0)}만원`);
  return b.max === Infinity ? `${fmt(b.min)} 이상` : `${fmt(b.min)} 이상 ${fmt(b.max)} 미만`;
}



const ProductCreate = () => {
  const [form, setForm] = useState({
    name: '',
    brand: '',
    category: '',
    probability: '',
    consumerPrice: '',
    price: '',
    shippingFee: '',
    shippingInfo: '',
    description: '',
    sourceLink: '',
    isSourceSoldOut: false,
    isVisible: true,
    statusDetail: '판매중',
  });

  const [mainImage, setMainImage] = useState(null);
  const [mainImagePreview, setMainImagePreview] = useState(null);
  const [additionalImages, setAdditionalImages] = useState([]);
  const [additionalPreviews, setAdditionalPreviews] = useState([]);
  const [boxOptions, setBoxOptions] = useState([]);
  const navigate = useNavigate();

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setForm((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
  };

  const handleMainImageChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.type === 'image/heic' || file.name.toLowerCase().endsWith('.heic')) {
      const converted = await heic2any({ blob: file, toType: 'image/jpeg' });
      setMainImagePreview(URL.createObjectURL(converted));
      setMainImage(converted);
    } else {
      setMainImagePreview(URL.createObjectURL(file));
      setMainImage(file);
    }
  };

  useEffect(() => {
    const fetchBoxList = async () => {
      try {
        const token = localStorage.getItem('token');
        const res = await axios.get('http://13.124.224.246:7778/api/box', {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (res.data.success && Array.isArray(res.data.boxes)) {
          setBoxOptions(res.data.boxes.map((box) => box.name));
        }
      } catch (err) {
        console.error('박스 목록 불러오기 실패:', err);
      }
    };
    fetchBoxList();
  }, []);

  const handleAdditionalImageChange = async (e) => {
    const files = Array.from(e.target.files || []);
    for (const file of files) {
      if (file.type === 'image/heic' || file.name.toLowerCase().endsWith('.heic')) {
        const converted = await heic2any({ blob: file, toType: 'image/jpeg' });
        setAdditionalImages((prev) => [...prev, converted]);
        setAdditionalPreviews((prev) => [...prev, URL.createObjectURL(converted)]);
      } else {
        setAdditionalImages((prev) => [...prev, file]);
        setAdditionalPreviews((prev) => [...prev, URL.createObjectURL(file)]);
      }
    }
  };

  const removeAdditionalImage = (index) => {
    setAdditionalImages((prev) => prev.filter((_, i) => i !== index));
    setAdditionalPreviews((prev) => prev.filter((_, i) => i !== index));
  };

  const moveImage = (fromIndex, toIndex) => {
    const imgs = [...additionalImages];
    const prevs = [...additionalPreviews];
    const [movedImg] = imgs.splice(fromIndex, 1);
    const [movedPrev] = prevs.splice(fromIndex, 1);
    imgs.splice(toIndex, 0, movedImg);
    prevs.splice(toIndex, 0, movedPrev);
    setAdditionalImages(imgs);
    setAdditionalPreviews(prevs);
  };

  const handleDragStart = (e, index) => {
    e.dataTransfer.setData('dragIndex', String(index));
  };

  const handleDropPreview = (e, index) => {
    const fromIndex = Number(e.dataTransfer.getData('dragIndex'));
    if (!Number.isNaN(fromIndex) && fromIndex !== index) moveImage(fromIndex, index);
  };

  /** ─────────── 카테고리/소비자가 변경 시 확률 자동 계산 ─────────── */
  const isAuto = useMemo(() => isAutoProbCategory(form.category), [form.category]);
  const probAuto = useMemo(
    () => (isAuto ? computeAutoProbability(form.category, form.consumerPrice) : null),
    [isAuto, form.category, form.consumerPrice]
  );

  useEffect(() => {
    if (!isAuto) return; // 자동 대상이 아니면 건드리지 않음
    // 소수점 유지(최대 3자리)해서 폼에 반영
    const value = (typeof probAuto === 'number' && !Number.isNaN(probAuto))
      ? String(Number(probAuto.toFixed(3)))
      : '';
    setForm(prev => ({ ...prev, probability: value }));
  }, [isAuto, probAuto]);

  const handleSubmit = async (e) => {
    e.preventDefault();

    const formData = new FormData();
    Object.entries(form).forEach(([key, value]) => {
      formData.append(key, value);
    });
    if (mainImage) formData.append('mainImage', mainImage);
    additionalImages.forEach((img) => formData.append('additionalImages', img));

    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(
        'http://13.124.224.246:7778/api/products/productCreate',
        formData,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (response.status === 200) {
        alert('상품이 성공적으로 등록되었습니다.');
        navigate('/products');
      } else {
        alert('상품 등록 실패: ' + (response.data?.message || '알 수 없는 오류'));
      }
    } catch (err) {
      console.error('상품 등록 에러:', err);
      alert('등록 중 오류 발생');
    }
  };

  const refundPreview = computeRefundPolicyPreview(form.consumerPrice);
  const bandLabel = isAuto ? findBandLabel(form.category, form.consumerPrice) : '-';

  return (
    <div className="product-create-container">
      <h2>상품 등록</h2>
      <form onSubmit={handleSubmit} className="product-create-form">
        {[
          { label: '상품 이름', name: 'name' },
          { label: '브랜드', name: 'brand' },
          { label: '카테고리', name: 'category' },
          { label: '소비자가', name: 'consumerPrice', type: 'number' },
          { label: '실구매가', name: 'price', type: 'number' },
          { label: '배송비', name: 'shippingFee', type: 'number' },
        ].map(({ label, name, type = 'text' }) => {
          if (name === 'category') {
            return (
              <div key={name} className="product-create-field">
                <label>{label}</label>
                <select
                  name="category"
                  value={form.category}
                  onChange={handleInputChange}
                  required
                >
                  <option value="" disabled hidden>
                    선택하세요
                  </option>
                  {boxOptions.map((boxName) => (
                    <option key={boxName} value={boxName}>
                      {boxName}
                    </option>
                  ))}
                </select>
              </div>
            );
          }
          return (
            <div key={name} className="product-create-field">
              <label>{label}</label>
              <input
                type={type}
                name={name}
                value={form[name]}
                onChange={handleInputChange}
                required={['name', 'category', 'price'].includes(name)}
              />
            </div>
          );
        })}

        <div className="product-create-field">
          <label>확률 (%) {isAuto && <span style={{fontSize:12, opacity:0.8}}>자동계산</span>}</label>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <input
              type="number"
              name="probability"
              value={form.probability}
              onChange={handleInputChange}
              min="0"
              max="100"
              step="0.001"         // 소수점 확률 지원
              required
              style={{ width: '120px' }}
              readOnly={isAuto}     // 자동 카테고리면 잠금
            />
            <span>%</span>
          </div>
          {isAuto && (
            <small style={{opacity:0.7}}>
              * 적용 구간: <b>{bandLabel}</b> → 자동 확률: <b>{form.probability}%</b>
            </small>
          )}
        </div>

        <div className="product-create-field">
          <label>노출 여부</label>
          <select
            name="isVisible"
            value={form.isVisible ? 'true' : 'false'}
            onChange={(e) =>
              setForm((prev) => ({ ...prev, isVisible: e.target.value === 'true' }))
            }
          >
            <option value="true">노출</option>
            <option value="false">비노출</option>
          </select>
        </div>

        <div className="product-create-field">
          <label>상태 상세</label>
          <select name="statusDetail" value={form.statusDetail} onChange={handleInputChange}>
            <option value="판매중">판매중</option>
            <option value="테스트">테스트</option>
            <option value="품절">품절</option>
            <option value="비노출">비노출</option>
          </select>
        </div>

        <div className="product-create-field">
          <label>발주처 링크</label>
          <input
            type="text"
            name="sourceLink"
            value={form.sourceLink}
            onChange={handleInputChange}
            placeholder="http, https로 시작하는 도메인을 입력하세요"
            required
          />
        </div>

        <div className="product-create-field">
          <label>발주처 품절 여부</label>
          <div style={{ display: 'flex', gap: '20px', marginTop: '8px' }}>
            <label>
              <input
                type="radio"
                name="isSourceSoldOut"
                value="true"
                checked={form.isSourceSoldOut === true}
                onChange={() => setForm((prev) => ({ ...prev, isSourceSoldOut: true }))}
              />{' '}
              예
            </label>
            <label>
              <input
                type="radio"
                name="isSourceSoldOut"
                value="false"
                checked={form.isSourceSoldOut === false}
                onChange={() => setForm((prev) => ({ ...prev, isSourceSoldOut: false }))}
              />{' '}
              아니오
            </label>
          </div>
        </div>

        <div className="product-create-field">
          <label>대표 이미지</label>
          <input type="file" onChange={handleMainImageChange} accept="image/*,.heic" />
          {mainImagePreview && <img src={mainImagePreview} alt="미리보기" className="image-preview" />}
        </div>

        <div className="product-create-field">
          <label>상세 이미지</label>
          <input type="file" multiple onChange={handleAdditionalImageChange} accept="image/*,.heic" />
          <div className="image-preview-list">
            {additionalPreviews.map((url, i) => (
              <div
                key={i}
                className="preview-item"
                draggable
                onDragStart={(e) => handleDragStart(e, i)}
                onDrop={(e) => handleDropPreview(e, i)}
                onDragOver={(e) => e.preventDefault()}
              >
                <img src={url} alt="상세 이미지" />
                <button type="button" onClick={() => removeAdditionalImage(i)}>
                  삭제
                </button>
              </div>
            ))}
          </div>
        </div>

        <div className="product-create-field">
          <label>상품 설명</label>
          <textarea name="description" value={form.description} onChange={handleInputChange} required />
        </div>

        {/* 자동 환급 정책 미리보기(읽기 전용) */}
        <div className="product-create-field">
          <label>환급 정책 (자동 계산)</label>
          <div className="refund-policy-preview" style={{ fontSize: 14, lineHeight: 1.6 }}>
            <div>
              기준: <b>{refundPreview.basis === 'box' ? '박스가' : '소비자가'}</b>
            </div>
            <div>
              환급률: <b>{refundPreview.rate}%</b> ({refundPreview.label})
            </div>
            <small style={{ opacity: 0.7 }}>* 등록/수정 시 서버에서 최종 확정됩니다.</small>
          </div>
        </div>

        <button type="submit" className="product-create-button">
          등록
        </button>
      </form>
    </div>
  );
};

export default ProductCreate;
